import React, { useEffect, useState } from 'react';
import { useWindowDimensions, Platform } from 'react-native';

import { useTranslation } from 'react-i18next';

import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { HeaderBackButton } from '@react-navigation/elements';

import { HStack, VStack, useToken } from '@gluestack-ui/themed';
import {
  Route, BookOpen, NotebookTabs, GraduationCap,
} from 'lucide-react-native';

import WebSidebar from '../WebSidebar';

import CourseScreen from '../../../student/course/CourseScreen';
import CourseScreenOld from '../../../student/course/CourseScreenOld';
import { ModuleScreen } from '../../../student/module/ModuleScreen';
import ModuleScreenOld from '../../../student/module/ModuleScreenOld';
import GradeScreen from '../../../student/grade/GradeScreen';
import TaskScreen from '../../../student/task/TaskScreen';
import { SlotMeetingScreen, slotMeetingScreenOptions } from '../../../student/meeting/SlotMeetingScreen';
import useDeepLinkHandler from './useDeepLinkHandler';
import { getColorForModule } from '../../utils/getColorForModule';
import { StudentPeopleNavigationProvider } from '../../../student/people/StudentPeopleNavigationContext';
import PeopleProfileScreen from '../../../student/people/ProfileScreen';
import MyConnectionsScreen from '../../../student/people/MyConnectionsScreen';

import { useSelectedModule } from '../../hooks/student/useSelectedModule';
import { useSelectedTask } from '../../hooks/student/useSelectedTask';
import { useUserFeatures } from '../../hooks/useUserFeatures';

const Stack = createNativeStackNavigator();

const StudentNavigation = ({ navigationRef }) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const showMobileNav = Platform.OS !== 'web' || width <= 768;
  const { module } = useSelectedModule();
  const { task } = useSelectedTask();
  const { isFeatureActive } = useUserFeatures();

  const isNewUiActive = isFeatureActive('student-new-ui');
  const headerBackgroundColor = useToken('colors', module ? `${getColorForModule(module)}400` : 'white');

  // icons for navigation
  const routeIconMapper = {
    CourseScreen: Route,
    ModuleScreen: BookOpen,
    TaskScreen: NotebookTabs,
    GradeScreen: GraduationCap,
  };

  useDeepLinkHandler({ navigationRef });

  // The oral-exam meeting (SlotMeetingScreen) is full-screen: its single
  // logo-left bar is MeetingChrome, so the module app-shell rail must not nest
  // behind it (mirrors the lecturer navigator hiding its sidebar there).
  const [activeRouteName, setActiveRouteName] = useState(null);
  useEffect(() => {
    if (!navigationRef?.current) return () => {};
    const syncRoute = () => setActiveRouteName(navigationRef.current?.getCurrentRoute?.()?.name ?? null);
    syncRoute();
    return navigationRef.current.addListener('state', syncRoute);
  }, [navigationRef]);
  const isFullScreenMeeting = activeRouteName === 'SlotMeetingScreen';

  return (
    <StudentPeopleNavigationProvider navigationRef={navigationRef}>
      <VStack flex={1}>
        <HStack flex={1}>
          <WebSidebar
            visible={!showMobileNav && !isFullScreenMeeting}
            icons={routeIconMapper}
            navigationRef={navigationRef}
          />
          <Stack.Navigator
            id="mainNavigation"
            initialRouteName="CourseScreen"
            screenOptions={{
              headerShown: showMobileNav,
              headerTintColor: 'black',
              animationEnabled: Platform.OS !== 'web',
              contentStyle: { backgroundColor: useToken('colors', 'bgApp') },
              unmountOnBlur: true,
              headerBackTitleVisible: false,
              headerBackButtonDisplayMode: 'minimal',
              headerStyle: {
                backgroundColor: headerBackgroundColor,
              },
            }}
          >
            <Stack.Screen
              name="CourseScreen"
              component={isNewUiActive ? CourseScreen : CourseScreenOld}
              options={() => ({
                headerShown: false,
                title: t('navigation.main.CourseScreen'),
                unmountOnBlur: false,
              })}
            />

            <Stack.Screen
              name="ModuleScreen"
              component={isNewUiActive ? ModuleScreen : ModuleScreenOld}
              options={() => ({
                headerShown: !isNewUiActive && showMobileNav,
                title: module?.title || t('navigation.main.ModuleScreen'),
                unmountOnBlur: true,
              })}
            />

            <Stack.Screen
              name="TaskScreen"
              component={TaskScreen}
              options={() => ({
                title: task?.title || t('navigation.main.TaskScreen'),
              })}
            />

            <Stack.Screen
              name="GradeScreen"
              component={GradeScreen}
              options={({ navigation }) => ({
                headerLeft: () => (
                  <HeaderBackButton
                    tintColor="black"
                    labelVisible={false}
                    onPress={() => navigation.navigate('CourseScreen')}
                  />
                ),
                title: t('navigation.main.GradeScreen'),
                unmountOnBlur: false,
              })}
            />

            <Stack.Screen
              name="SlotMeetingScreen"
              component={SlotMeetingScreen}
              options={() => slotMeetingScreenOptions()}
            />

            {isFeatureActive('student-connections') && (
            <Stack.Screen
              name="PeopleProfile"
              component={PeopleProfileScreen}
              options={() => ({
                title: t('people.profile.title'),
                unmountOnBlur: true,
              })}
            />
            )}

            {isFeatureActive('student-connections') && (
            <Stack.Screen
              name="MyConnections"
              component={MyConnectionsScreen}
              options={() => ({
                title: t('people.connections.title'),
                unmountOnBlur: true,
              })}
            />
            )}
          </Stack.Navigator>
        </HStack>
      </VStack>
    </StudentPeopleNavigationProvider>
  );
};

export default StudentNavigation;
